import debounce from 'lodash/debounce';
import { useEffect, useLayoutEffect, useState, useRef, RefObject, ReactElement } from 'react';

type useResponsiveNavType = [ReactElement[], ReactElement[], RefObject<HTMLElement>];

const useResponsiveNav = (elements): useResponsiveNavType => {
  const [running, setRunning] = useState(true);
  const [dropdownItems, setDropdownItems] = useState(0);
  const navRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const offsetWidth = (nav && nav.clientWidth - nav.scrollWidth) || 0;
    // 'clientWidth' and 'scrollWidth' suffer from a bug where they are 1px off
    // when using Safari and user zoomed in. By setting the threshold to -1, we
    // can avoid an infinite rerender loop.
    const hasOverflow = offsetWidth < -1;

    // Returns a higher number if too many items
    // are overflowing the wrapper. This makes
    // the hook run less than half the times
    const defineDynamicIncrement = () => {
      const LARGE_OFFSET_AVERAGE = -1500;
      const MEDIUM_OFFSET_AVERAGE = -500;

      if (offsetWidth < LARGE_OFFSET_AVERAGE) return 10;
      if (offsetWidth < MEDIUM_OFFSET_AVERAGE) return 5;
      return 1;
    };

    if (hasOverflow && running) {
      setDropdownItems(s => s + defineDynamicIncrement());
    }
    if (!hasOverflow) {
      setRunning(false);
    }
  }, [dropdownItems, running]);

  useEffect(() => {
    const debouncedSetRunning = debounce(() => {
      setDropdownItems(0);
      setRunning(true);
    }, 100);

    window.addEventListener('resize', debouncedSetRunning);

    return () => window.removeEventListener('resize', debouncedSetRunning);
  }, []);

  return [
    elements.slice(0, elements.length - dropdownItems),
    elements.slice(elements.length - dropdownItems, elements.length),
    navRef,
  ];
};

export default useResponsiveNav;
